<i18n locale="en">
{
  "title": "Image Analysis",
  "promotion": {
    "success": "Image set as primary",
    "error": "Failed to set image as primary"
  }
}
</i18n>

<template>
  <OwnDialog v-model="isVisible" size="sm" @cancel="onClose">
    <template #heading>
      <OwnDialogHeading :title="t('title')" @cancel="onClose" />
    </template>

    <OwnDialogContent>
      <ImageAnalysisPanel
        :is-loading="isLoading"
        :has-issues="hasIssues"
        :findings="findings"
        :is-reanalyzing="isReanalyzing"
        @reanalyze="handleTriggerAnalysis"
      />

      <OwnRule type="dashed" class="my-4" />

      <ImageGalleryPanel
        :items="combinedGalleryItems"
        @item-click="onGalleryItemClick"
        @generate-click="onGenerateClick"
        @item-retry="onRetryClick"
      />
    </OwnDialogContent>
  </OwnDialog>

  <ImagePreviewOverlay
    v-model="showPreview"
    :media="previewMedia"
    :is-promoting="isPromoting"
    @set-as-primary="onSetAsPrimaryClick"
  />

  <PromoteConfirmationDialog
    v-model="showConfirmation"
    :item-name="menuItemName"
    :processing="isPromoting"
    @confirm="handlePromote"
  />
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { OwnDialog, OwnDialogContent, OwnDialogHeading } from '@/ui/OwnDialog'
import { OwnRule } from '@/ui/OwnRule'
import { useAnalytics } from '@/utils/useAnalytics'
import { useNotify } from '@/utils/useNotify'

import ImageAnalysisPanel from './ImageAnalysisPanel.vue'
import ImageGalleryPanel from './ImageGalleryPanel.vue'
import ImagePreviewOverlay from './ImagePreviewOverlay.vue'
import PromoteConfirmationDialog from './PromoteConfirmationDialog.vue'
import type { GalleryItem, Media, MediaFinding } from './types'
import { useImageGallery } from './useImageGallery'
import { useImageGeneration } from './useImageGeneration'
import { useMediaAnalysis } from './useMediaAnalysis'

interface Props {
  modelValue: boolean
  menuItemId: string
  menuItemName: string
  brandId: string
  currentImageUrl: string
  itemDescription?: string | null
}

interface Emits {
  (event: 'update:modelValue', value: boolean): void
  (event: 'promoted', imageUrl: string): void
}

const props = withDefaults(defineProps<Props>(), {
  itemDescription: null,
})

const emit = defineEmits<Emits>()

const { t } = useI18n()
const notify = useNotify()
const { track } = useAnalytics()

const previewMedia = ref<Media | null>(null)
const showPreview = ref(false)
const showConfirmation = ref(false)
const isPromoting = ref(false)
const previousPrimaryMediaId = ref<string | null>(null)

const mediaAnalysis = useMediaAnalysis({
  brandId: toRef(props, 'brandId'),
  currentImageUrl: toRef(props, 'currentImageUrl'),
  itemDescription: toRef(props, 'itemDescription'),
  itemName: toRef(props, 'menuItemName'),
  menuItemId: toRef(props, 'menuItemId'),
})

const imageGallery = useImageGallery({
  enabled: ref(true),
  menuItemId: toRef(props, 'menuItemId'),
})

const imageGeneration = useImageGeneration({
  analysisResults: computed(() => {
    if (!mediaAnalysis.analysis.value) return null
    const problems: Record<string, string> = {}
    mediaAnalysis.problemFindings.value.forEach((finding) => {
      problems[finding.name] = finding.reason
    })
    return problems
  }),
  brandId: toRef(props, 'brandId'),
  itemDescription: toRef(props, 'itemDescription'),
  itemName: toRef(props, 'menuItemName'),
  menuItemId: toRef(props, 'menuItemId'),
  parentMediaUrl: computed(() => mediaAnalysis.primaryMedia.value?.url ?? null),
})

const isLoading = computed(() => mediaAnalysis.isLoading.value)
const isReanalyzing = ref(false)
const hasIssues = computed(() => mediaAnalysis.hasIssues.value)
const findings = computed<MediaFinding[]>(
  () => mediaAnalysis.problemFindings.value
)

onMounted(() => {
  let hasTriggered = false
  const unwatch = watch(
    () => ({
      hasAnalysis: !!mediaAnalysis.analysis.value,
      hasMedia: !!mediaAnalysis.primaryMedia.value,
      isLoading: mediaAnalysis.isLoading.value,
    }),
    ({ hasMedia, isLoading, hasAnalysis }) => {
      if (hasTriggered) return
      if (hasMedia && !isLoading) {
        hasTriggered = true
        if (!hasAnalysis) {
          void mediaAnalysis.triggerAnalysis()
        }
        void nextTick(() => unwatch())
      }
    },
    { immediate: true }
  )
})

const combinedGalleryItems = computed<GalleryItem[]>(() => {
  const parentImageUrl = mediaAnalysis.primaryMedia.value?.url ?? null

  const pendingItems: GalleryItem[] =
    imageGeneration.pendingGenerations.value.map((gen) => ({
      failedAt: gen.failedAt,
      id: gen.id,
      media: null,
      parentImageUrl: parentImageUrl ?? undefined,
      state: gen.status,
    }))

  const galleryItems = imageGallery.galleryItems.value.filter(
    (item) => item.state !== 'placeholder'
  )

  const placeholder: GalleryItem = {
    id: 'placeholder',
    media: null,
    state: 'placeholder',
  }

  return [placeholder, ...pendingItems, ...galleryItems]
})

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    emit('update:modelValue', value)
  },
})

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      track('image_analysis_modal_opened', {
        hasExistingAnalysis: !!mediaAnalysis.analysis.value,
        menuItemId: props.menuItemId,
      })
    }
  }
)

watch(
  () => mediaAnalysis.primaryMedia.value?.id,
  (newId) => {
    if (newId) {
      previousPrimaryMediaId.value = newId
    }
  },
  { immediate: true }
)

/**
 * Closes the modal
 */
function onClose(): void {
  emit('update:modelValue', false)
}

/**
 *
 */
async function handleTriggerAnalysis(): Promise<void> {
  isReanalyzing.value = true
  try {
    await mediaAnalysis.triggerAnalysis()
  } finally {
    isReanalyzing.value = false
  }
}

/**
 * Handles clicking on a gallery item
 * @param {GalleryItem} item - The gallery item that was clicked
 */
function onGalleryItemClick(item: GalleryItem): void {
  if (item.state === 'ready' && item.media) {
    track('image_preview_opened', {
      mediaId: item.media.id,
      menuItemId: props.menuItemId,
    })
    previewMedia.value = item.media
    showPreview.value = true
  }
}

/**
 * Handles clicking the Set as Primary button in the preview overlay
 */
function onSetAsPrimaryClick(): void {
  showConfirmation.value = true
}

/**
 * Handles confirming the promotion of an image to primary
 */
async function handlePromote(): Promise<void> {
  if (!previewMedia.value || isPromoting.value) return

  isPromoting.value = true
  const promotedImageUrl = previewMedia.value.url!
  try {
    await OlympusClient.media.v1.menuItem.menuItem.setPrimary({
      body: { mediaId: previewMedia.value.id },
      params: { itemId: props.menuItemId },
    })

    track('image_promoted_to_primary', {
      mediaId: previewMedia.value.id,
      menuItemId: props.menuItemId,
      previousPrimaryMediaId: previousPrimaryMediaId.value,
    })

    await imageGallery.refetch()
    showPreview.value = false
    showConfirmation.value = false
    notify(t('promotion.success'), 'success')
    emit('promoted', promotedImageUrl)
    previewMedia.value = null
  } catch (error) {
    logError(error)
    notify(t('promotion.error'), 'error')
  } finally {
    isPromoting.value = false
  }
}

/**
 * Handles clicking on the generate placeholder
 */
function onGenerateClick(): void {
  void imageGeneration.startGeneration().then(async () => {
    await imageGallery.refetch()
  })
}

/**
 * Handles clicking on the retry button for a failed generation
 * @param {GalleryItem} item - The failed gallery item
 */
function onRetryClick(item: GalleryItem): void {
  void imageGeneration.retryGeneration(item.id).then(async () => {
    await imageGallery.refetch()
  })
}
</script>

<style scoped></style>
